import FetchClient, { type ApiResponse } from "./FetchClient";

export interface SearchProvider {
  type: string;
  label: string;
  enabled: boolean;
  trafficPercent: number;
}

export type SearchProviderRoutingMode = "fallback" | "percentage";

export interface SearchProviderConfig {
  routingMode: SearchProviderRoutingMode;
  providers: SearchProvider[];
}

export const getSearchProviders = async (): Promise<SearchProvider[]> => {
  const response = await FetchClient.get<ApiResponse<SearchProvider[]>>("/admin/config/search-providers");
  return response.data;
};

export const updateSearchProviders = async (providers: SearchProvider[]): Promise<SearchProvider[]> => {
  const response = await FetchClient.put<ApiResponse<SearchProvider[]>>("/admin/config/search-providers", providers);
  return response.data;
};

export const getSearchProviderConfig = async (): Promise<SearchProviderConfig> => {
  const response = await FetchClient.get<ApiResponse<SearchProviderConfig>>("/admin/config/search-provider-routing");
  return response.data;
};

export const updateSearchProviderConfig = async (config: SearchProviderConfig): Promise<SearchProviderConfig> => {
  const response = await FetchClient.put<ApiResponse<SearchProviderConfig>>("/admin/config/search-provider-routing", config);
  return response.data;
};

export interface ProductSearchRadiusConfig {
  walmartMiles: number;
  homeDepotMiles: number;
}

export const getProductSearchRadius = async (): Promise<ProductSearchRadiusConfig> => {
  const response = await FetchClient.get<ApiResponse<ProductSearchRadiusConfig>>("/admin/config/product-search-radius");
  return response.data;
};

export const updateProductSearchRadius = async (config: ProductSearchRadiusConfig): Promise<ProductSearchRadiusConfig> => {
  const response = await FetchClient.put<ApiResponse<ProductSearchRadiusConfig>>("/admin/config/product-search-radius", config);
  return response.data;
};

export type AffiliateProvider = "" | "amazon" | "impact" | "mavely";

export interface ProductAffiliateConfig {
  retailer: string;
  displayName: string;
  enabled: boolean;
  provider: AffiliateProvider;
}

export interface AffiliateProviderOption {
  type: Exclude<AffiliateProvider, "">;
  label: string;
}

export interface ProductAffiliatesAdminResponse {
  affiliates: ProductAffiliateConfig[];
  providers: AffiliateProviderOption[];
  configMissing: boolean;
}

export const getProductAffiliates = async (): Promise<ProductAffiliatesAdminResponse> => {
  const response = await FetchClient.get<ApiResponse<ProductAffiliatesAdminResponse>>("/admin/config/product-affiliates");
  return response.data;
};

export const updateProductAffiliates = async (
  affiliates: ProductAffiliateConfig[],
): Promise<ProductAffiliatesAdminResponse> => {
  const payload = affiliates.map(({ retailer, enabled, provider }) => ({ retailer, enabled, provider }));
  const response = await FetchClient.put<ApiResponse<ProductAffiliatesAdminResponse>>("/admin/config/product-affiliates", payload);
  return response.data;
};

export interface RetailerWebhookConfig {
  retailer: string;
  displayName: string;
  webhookId: string;
}

export interface RetailerWebhooksAdminResponse {
  retailers: RetailerWebhookConfig[];
  configMissing: boolean;
}

export const getRetailerWebhooks = async (): Promise<RetailerWebhooksAdminResponse> => {
  const response = await FetchClient.get<ApiResponse<RetailerWebhooksAdminResponse>>("/admin/config/retailer-webhooks");
  return response.data;
};

export const updateRetailerWebhooks = async (
  retailers: RetailerWebhookConfig[],
): Promise<RetailerWebhooksAdminResponse> => {
  const payload = retailers.map(({ retailer, webhookId }) => ({ retailer, webhookId }));
  const response = await FetchClient.put<ApiResponse<RetailerWebhooksAdminResponse>>("/admin/config/retailer-webhooks", payload);
  return response.data;
};

export interface LeadDiscordChannelConfig {
  channelId: string;
  retailer: string;
  category?: string;
}

export interface DiscordLeadChannelsAdminResponse {
  channels: LeadDiscordChannelConfig[];
  configMissing: boolean;
}

export const getDiscordLeadChannels = async (): Promise<DiscordLeadChannelsAdminResponse> => {
  const response = await FetchClient.get<ApiResponse<DiscordLeadChannelsAdminResponse>>("/admin/config/discord-lead-channels");
  return response.data;
};

export const updateDiscordLeadChannels = async (
  channels: LeadDiscordChannelConfig[],
): Promise<DiscordLeadChannelsAdminResponse> => {
  const response = await FetchClient.put<ApiResponse<DiscordLeadChannelsAdminResponse>>("/admin/config/discord-lead-channels", channels);
  return response.data;
};

export interface StoreTabDaysConfig {
  days: Record<string, number>;
  retailers: { retailer: string; defaultDays: number }[];
  minDays: number;
  maxDays: number;
}

export const getStoreTabDays = async (): Promise<StoreTabDaysConfig> => {
  const response = await FetchClient.get<ApiResponse<StoreTabDaysConfig>>("/admin/config/store-tab-days");
  return response.data;
};

export const updateStoreTabDays = async (days: Record<string, number>): Promise<StoreTabDaysConfig> => {
  const response = await FetchClient.put<ApiResponse<StoreTabDaysConfig>>("/admin/config/store-tab-days", days);
  return response.data;
};
